import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnChanges,
  OnInit,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { finalize, takeUntil } from 'rxjs';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { FeedbackService } from 'src/app/core/services/feedback.service';
import { PatientService } from 'src/app/core/services/patient.service';
import {
  duplicateMatchFieldLabel,
  getDuplicateMatchFieldValue,
} from 'src/app/core/duplicate-match/patient-duplicate-match-core';
import { DuplicatePairRow } from 'src/app/core/services/patient-duplicate-match.service';
import { UserService } from 'src/app/core/services/user.service';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-duplicate-alert-list-item',
  templateUrl: './duplicate-alert-list-item.component.html',
  styleUrls: ['./duplicate-alert-list-item.component.scss']
})
export class DuplicateAlertListItemComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() row!: DuplicatePairRow;

  mergeBusy = false;

  /**
   * Synced from UserService when profile/JWT identity is available (SSR starts false; updates after client + profile).
   */
  mergeUiEnabled = false;

  /** Derived from `row` — avoid template function calls each CD cycle */
  matchSummaryText = '';
  /** Matched fields only — values for the primary pane (inside button). */
  primaryPaneMatchLines: { label: string; value: string }[] = [];
  /** Matched fields only — values for the other pane (inside button). */
  otherPaneMatchLines: { label: string; value: string }[] = [];
  private pairKey = '';

  constructor(
    private patientService: PatientService,
    public userService: UserService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: object,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['row']) {
      this.refreshRowDerivedState();
    }
  }

  ngOnInit(): void {
    this.refreshRowDerivedState();
    this.syncMergeUiEnabled();
    this.userService.userProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.syncMergeUiEnabled();
        this.cdr.detectChanges();
      });

    if (isPlatformBrowser(this.platformId)) {
      queueMicrotask(() => {
        this.syncMergeUiEnabled();
        this.cdr.detectChanges();
      });
    }
  }

  private syncMergeUiEnabled(): void {
    this.mergeUiEnabled = this.userService.isMergeAuthorizedUser;
  }

  private refreshRowDerivedState(): void {
    const row = this.row;
    if (!row) {
      this.matchSummaryText = '';
      this.primaryPaneMatchLines = [];
      this.otherPaneMatchLines = [];
      this.pairKey = '';
      return;
    }
    const keys = row.matchedLabels ?? [];
    this.primaryPaneMatchLines = keys.map((key) => ({
      label: duplicateMatchFieldLabel(key),
      value: getDuplicateMatchFieldValue(row.primaryPatient, key),
    }));
    this.otherPaneMatchLines = keys.map((key) => ({
      label: duplicateMatchFieldLabel(key),
      value: getDuplicateMatchFieldValue(row.otherPatient, key),
    }));
    this.matchSummaryText = this.buildMatchSummaryText(row);
    this.pairKey = this.buildPairKey(row);
  }

  otherPatientLabel(p: any): string {
    const num = p?.patientNumber ?? '—';
    return `${p?.surname ?? ''}, ${p?.name ?? ''} – ${num}`.trim();
  }

  private buildMatchSummaryText(row: DuplicatePairRow): string {
    const other = this.otherPatientLabel(row.otherPatient);
    const fields = this.formatMatchedLabels(row.matchedLabels);
    switch (row.pairKind) {
      case 'invite-file':
        return `This invite matches ${other} on ${fields}.`;
      case 'invite-invite':
        return `This invite matches ${other} on ${fields}.`;
      case 'file-file':
        return `This file matches ${other} on ${fields}.`;
      default:
        return '';
    }
  }

  private formatMatchedLabels(labels: string[]): string {
    if (!labels?.length) {
      return 'several fields';
    }
    if (labels.length === 1) {
      return labels[0];
    }
    if (labels.length === 2) {
      return `${labels[0]} and ${labels[1]}`;
    }
    return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
  }

  navigateToPatient(event: MouseEvent, patient: any) {
    event.preventDefault();
    event.stopPropagation();
    const id = patient?.id;
    if (!id) {
      return;
    }
    this.patientService.getPatientById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.router.navigate(['/patients', {
            outlets: {
              patient: ['file', id],
            }
          }]);
        },
        error: () => {
          this.showToastMessage('There was an error loading the patient file. Please try again.');
        }
      });
  }

  private buildPairKey(row: DuplicatePairRow): string {
    const a = row.primaryPatient?.id;
    const b = row.otherPatient?.id;
    if (!a || !b) {
      return '';
    }
    return [a, b].sort().join('|');
  }

  onDragStart(event: DragEvent, patient: { id?: string }) {
    if (!this.mergeUiEnabled || this.mergeBusy) {
      event.preventDefault();
      return;
    }
    const id = patient?.id;
    if (!id) {
      event.preventDefault();
      return;
    }
    event.dataTransfer?.setData(
      'application/json',
      JSON.stringify({ patientId: id, pairKey: this.pairKey }),
    );
    event.dataTransfer!.effectAllowed = 'move';
  }

  onDragOver(event: DragEvent) {
    if (!this.mergeUiEnabled || this.mergeBusy) {
      return;
    }
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
  }

  async onDrop(event: DragEvent, targetPatient: { id?: string }) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.mergeUiEnabled || this.mergeBusy) {
      return;
    }
    const raw = event.dataTransfer?.getData('application/json');
    if (!raw || !targetPatient?.id) {
      return;
    }
    let data: { patientId: string; pairKey: string };
    try {
      data = JSON.parse(raw);
    } catch {
      return;
    }
    if (!data.patientId || data.pairKey !== this.pairKey) {
      return;
    }
    if (data.patientId === targetPatient.id) {
      return;
    }

    const fromPatient =
      this.row.primaryPatient?.id === data.patientId
        ? this.row.primaryPatient
        : this.row.otherPatient?.id === data.patientId
          ? this.row.otherPatient
          : null;
    if (!fromPatient) {
      return;
    }

    const fromLabel = this.otherPatientLabel(fromPatient);
    const toLabel = this.otherPatientLabel(targetPatient);
    const ok = await this.showConfirmationDialog(
      'Merge duplicate patients?',
      `Merge ${fromLabel} into ${toLabel}? This action cannot be undone.`,
      undefined,
      'Cancel',
      'Merge',
      false,
    );
    if (!ok) {
      return;
    }

    this.mergeBusy = true;
    this.patientService
      .mergeDuplicatePatients(data.patientId, targetPatient.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.mergeBusy = false;
        }),
      )
      .subscribe({
        next: (res) => {
          if (res?.success) {
            this.showToastMessage('Patients merged successfully.');
          } else {
            this.showToastMessage(res?.message || 'Merge failed.');
          }
        },
        error: (err) => {
          const msg = err?.error?.message || err?.message || 'Merge failed.';
          this.showToastMessage(typeof msg === 'string' ? msg : 'Merge failed.');
        },
      });
  }
}
